import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { getDocument } from '@atlaskit/browser-apis';

export type MountPosition = 'after' | 'before' | 'inside';
export type RenderMode = 'portal' | 'inline';

export type UseMountToHtmlElementOptions = {
	selectors?: string;
	as?: RenderMode;
	position?: MountPosition;
	observeRoot?: Element | Document;
	rootDocument?: Document;
};

export type RenderFunction = (children: ReactNode) => ReactNode;

/**
 * Mounts React content near a DOM element found by CSS selector.
 * Used to position spotlight popovers relative to their target element.
 */
export function useMountToHtmlElement(params?: UseMountToHtmlElementOptions): RenderFunction {
	const { as = 'portal', selectors, position = 'after', observeRoot, rootDocument } = params ?? {};

	const targetElement = useElement({ selectors, observeRoot, rootDocument });
	const renderPortal = usePortalNearElement({ targetElement, position });
	const renderInline = useInlineNearElement({ targetElement, position });

	if (as === 'inline') {
		return renderInline;
	}

	return renderPortal;
}

function useElement({
	selectors,
	observeRoot,
	rootDocument,
}: {
	selectors?: string;
	observeRoot?: Element | Document;
	rootDocument?: Document;
}): Element | undefined {
	const knownEls = useRef<Record<string, Element | undefined>>({});
	const [el, setEl] = useState<Element | undefined>();

	useEffect(() => {
		const doc = rootDocument ?? getDocument();

		if (!doc || !selectors) {
			setEl(undefined);
			return;
		}

		if (knownEls.current[selectors] !== undefined) {
			setEl(knownEls.current[selectors]);
			return;
		}

		const element = doc.querySelector(selectors) ?? undefined;
		knownEls.current[selectors] = element;
		setEl(element);

		if (observeRoot) {
			const observer = new MutationObserver(() => {
				const foundElement = doc.querySelector(selectors);
				if (foundElement) {
					setEl(foundElement);
					observer.disconnect();
				}
			});
			observer.observe(observeRoot, { childList: true, subtree: true });
			return () => observer.disconnect();
		}
	}, [selectors, observeRoot, rootDocument]);

	return el;
}

function usePortalNearElement({
	targetElement,
	position,
}: {
	targetElement: Element | undefined;
	position: MountPosition;
}): RenderFunction {
	const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!getDocument() || !targetElement) {
			setPortalContainer(null);
			return;
		}

		const container = targetElement.ownerDocument.createElement('div');
		container.style.position = 'absolute';

		if (position === 'after') {
			targetElement.parentNode?.insertBefore(container, targetElement.nextSibling);
		} else if (position === 'before') {
			targetElement.parentNode?.insertBefore(container, targetElement);
		} else {
			targetElement.appendChild(container);
		}

		setPortalContainer(container);
		return () => container.remove();
	}, [targetElement, position]);

	return (children: ReactNode) =>
		portalContainer ? createPortal(children, portalContainer) : null;
}

function useInlineNearElement({
	targetElement,
	position,
}: {
	targetElement: Element | undefined;
	position: MountPosition;
}): RenderFunction {
	const [inlineContainer, setInlineContainer] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!getDocument() || !targetElement) {
			setInlineContainer(null);
			return;
		}

		const container = targetElement.ownerDocument.createElement('div');

		if (position === 'after') {
			targetElement.parentNode?.insertBefore(container, targetElement.nextSibling);
		} else if (position === 'before') {
			targetElement.parentNode?.insertBefore(container, targetElement);
		} else {
			targetElement.appendChild(container);
		}

		setInlineContainer(container);
		return () => container.remove();
	}, [targetElement, position]);

	return (children: ReactNode) =>
		inlineContainer ? createPortal(children, inlineContainer) : null;
}
